import { useMemo, useState } from 'react'
import { AlertCircle, CheckCircle2, FilePlus2, Hash, ListChecks, Plus, Trash2 } from 'lucide-react'
import type { Requirement } from '@ispot/shared'
import type { ViewContainerProps } from './sharedProps'

interface DraftStory {
  title: string
  description: string[]
  criteria: string[]
}

interface DraftEpic {
  title: string
  description: string[]
  stories: DraftStory[]
}

interface ParsedDraft {
  epics: DraftEpic[]
  counts: {
    epics: number
    stories: number
    criteria: number
  }
}

const PLACEHOLDER = `# Customer onboarding
- Invite teammates during setup
+ Invitation email is sent from the workspace owner
+ Invitee lands in the same project
- Resume setup later
+ Progress is restored after sign-in`

function ensureEpic(epics: DraftEpic[]): DraftEpic {
  if (epics.length === 0) {
    epics.push({ title: 'General requirements', description: [], stories: [] })
  }
  return epics[epics.length - 1]
}

function ensureStory(epic: DraftEpic): DraftStory {
  if (epic.stories.length === 0) {
    epic.stories.push({ title: 'Untitled story', description: [], criteria: [] })
  }
  return epic.stories[epic.stories.length - 1]
}

function parseDraft(raw: string): ParsedDraft {
  const epics: DraftEpic[] = []
  let currentEpic: DraftEpic | null = null
  let currentStory: DraftStory | null = null

  for (const line of raw.split(/\r?\n/)) {
    const trimmed = line.trim()
    if (!trimmed) continue

    const epicMatch = trimmed.match(/^#{1,6}\s+(.+)$/)
    if (epicMatch) {
      currentEpic = {
        title: epicMatch[1].trim() || 'Untitled epic',
        description: [],
        stories: [],
      }
      epics.push(currentEpic)
      currentStory = null
      continue
    }

    const storyMatch = trimmed.match(/^(-|\*|\u2022)\s+(.+)$/)
    if (storyMatch) {
      currentEpic = currentEpic ?? ensureEpic(epics)
      currentStory = {
        title: storyMatch[2].trim() || 'Untitled story',
        description: [],
        criteria: [],
      }
      currentEpic.stories.push(currentStory)
      continue
    }

    const criterionMatch = trimmed.match(/^\+\s+(.+)$/)
    if (criterionMatch) {
      currentEpic = currentEpic ?? ensureEpic(epics)
      currentStory = currentStory ?? ensureStory(currentEpic)
      currentStory.criteria.push(criterionMatch[1].trim())
      continue
    }

    if (currentStory) currentStory.description.push(trimmed)
    else {
      currentEpic = currentEpic ?? ensureEpic(epics)
      currentEpic.description.push(trimmed)
    }
  }

  return {
    epics,
    counts: {
      epics: epics.length,
      stories: epics.reduce((sum, epic) => sum + epic.stories.length, 0),
      criteria: epics.reduce((sum, epic) => sum + epic.stories.reduce((s, story) => s + story.criteria.length, 0), 0),
    },
  }
}

function plural(count: number, one: string, many: string) {
  return `${count} ${count === 1 ? one : many}`
}

export function DraftView({
  createBlock,
  updateRequirement,
  errorBanner,
}: ViewContainerProps) {
  const [draft, setDraft] = useState('')
  const [isImporting, setIsImporting] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)
  const [importedMessage, setImportedMessage] = useState<string | null>(null)

  const parsed = useMemo(() => parseDraft(draft), [draft])
  const hasParsedItems = parsed.counts.epics > 0 || parsed.counts.stories > 0 || parsed.counts.criteria > 0

  async function importDraft() {
    if (!hasParsedItems || isImporting) return
    setIsImporting(true)
    setLocalError(null)
    setImportedMessage(null)

    try {
      for (const epic of parsed.epics) {
        const createdEpic = await createBlock({
          type: 'epic',
          name: epic.title,
          section: 'functional',
        })

        if (epic.description.length > 0) {
          await updateRequirement(createdEpic.id, {
            description: epic.description.join('\n'),
          } satisfies Partial<Requirement>)
        }

        for (const story of epic.stories) {
          const createdStory = await createBlock({
            type: 'story',
            name: story.title,
            parentId: createdEpic.id,
          })

          const patch: Partial<Requirement> = {}
          if (story.description.length > 0) patch.description = story.description.join('\n')
          if (story.criteria.length > 0) {
            patch.metadata = {
              acceptanceCriteria: story.criteria.map(text => ({ text, done: false, met: false })),
            }
          }
          if (Object.keys(patch).length > 0) {
            await updateRequirement(createdStory.id, patch)
          }
        }
      }

      setImportedMessage(
        `Imported ${plural(parsed.counts.epics, 'epic', 'epics')}, ${plural(parsed.counts.stories, 'story', 'stories')}, ${plural(parsed.counts.criteria, 'criterion', 'criteria')}.`,
      )
      setDraft('')
    } catch {
      setLocalError('Could not import this draft. Nothing was cleared, so you can try again.')
    } finally {
      setIsImporting(false)
    }
  }

  return (
    <div className="flex-1 min-w-0 overflow-y-auto" style={{ background: 'var(--color-warm-50)' }}>
      {errorBanner}
      <div className="mx-auto w-full max-w-[1120px] px-6 py-7">
        <div className="mb-6 flex items-start gap-4">
          <div className="min-w-0 flex-1">
            <div
              className="mb-2 font-mono uppercase"
              style={{ color: 'var(--color-fg-muted)', fontSize: 10, letterSpacing: '0.08em' }}
            >
              Quick capture
            </div>
            <h2
              className="m-0"
              style={{
                color: 'var(--color-fg)',
                fontFamily: 'var(--font-display)',
                fontSize: 24,
                fontWeight: 600,
                lineHeight: 1.15,
              }}
            >
              Draft requirements
            </h2>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setDraft('')
                setLocalError(null)
                setImportedMessage(null)
              }}
              disabled={!draft || isImporting}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-45 focus-visible:outline-none focus-visible:ring-2"
              style={{
                border: '1px solid var(--color-warm-300)',
                background: 'var(--color-bg-card)',
                color: 'var(--color-fg-secondary)',
                ['--tw-ring-color' as string]: 'var(--color-accent)',
              }}
            >
              <Trash2 className="h-4 w-4" />
              Clear
            </button>
            <button
              type="button"
              onClick={() => { void importDraft() }}
              disabled={!hasParsedItems || isImporting}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-45 focus-visible:outline-none focus-visible:ring-2"
              style={{
                border: '1px solid var(--color-accent-500)',
                background: 'var(--color-accent-500)',
                color: 'var(--color-neutral-50)',
                ['--tw-ring-color' as string]: 'var(--color-accent)',
              }}
            >
              <FilePlus2 className="h-4 w-4" />
              {isImporting ? 'Importing' : 'Import'}
            </button>
          </div>
        </div>

        {(localError || importedMessage) && (
          <div
            className="mb-4 flex items-center gap-2 rounded-lg px-3 py-2 text-sm"
            style={{
              background: localError ? 'var(--color-danger-bg)' : 'var(--color-success-bg)',
              color: localError ? 'var(--color-danger-fg)' : 'var(--color-success-fg)',
              border: `1px solid ${localError ? 'color-mix(in oklab, var(--color-danger) 22%, transparent)' : 'color-mix(in oklab, var(--color-success) 22%, transparent)'}`,
            }}
          >
            {localError ? (
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
            ) : (
              <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
            )}
            <span>{localError ?? importedMessage}</span>
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
          <section
            className="min-w-0 rounded-xl"
            style={{
              background: 'var(--color-bg-card)',
              border: '1px solid var(--color-warm-200)',
              boxShadow: 'var(--shadow-xs)',
            }}
          >
            <textarea
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value)
                if (localError) setLocalError(null)
                if (importedMessage) setImportedMessage(null)
              }}
              placeholder={PLACEHOLDER}
              spellCheck={false}
              className="block h-[min(68vh,760px)] min-h-[440px] w-full resize-none rounded-xl border-0 bg-transparent px-7 py-6 outline-none"
              style={{
                color: 'var(--color-fg)',
                fontFamily: 'var(--font-body)',
                fontSize: 16,
                lineHeight: 1.75,
                caretColor: 'var(--color-accent-500)',
              }}
            />
          </section>

          <aside className="min-w-0">
            <div
              className="sticky top-6 rounded-xl p-4"
              style={{
                background: 'var(--color-bg-card)',
                border: '1px solid var(--color-warm-200)',
                boxShadow: 'var(--shadow-xs)',
              }}
            >
              <div className="grid grid-cols-3 gap-2">
                <DraftCount icon={Hash} label="Epics" count={parsed.counts.epics} />
                <DraftCount icon={ListChecks} label="Stories" count={parsed.counts.stories} />
                <DraftCount icon={Plus} label="Criteria" count={parsed.counts.criteria} />
              </div>

              <div className="mt-5 space-y-4">
                {parsed.epics.length === 0 ? (
                  <p className="m-0 text-sm leading-6" style={{ color: 'var(--color-fg-muted)' }}>
                    Start with a heading, add stories as bullets, then add criteria with plus signs.
                  </p>
                ) : (
                  parsed.epics.slice(0, 6).map((epic, index) => (
                    <div key={`${epic.title}:${index}`} className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span
                          className="font-mono text-[11px] font-semibold"
                          style={{ color: 'var(--color-solution-700)' }}
                        >
                          #
                        </span>
                        <div className="truncate text-sm font-semibold" style={{ color: 'var(--color-fg)' }}>
                          {epic.title}
                        </div>
                      </div>
                      {epic.stories.length > 0 && (
                        <div className="mt-2 space-y-1 pl-5">
                          {epic.stories.slice(0, 4).map((story, storyIndex) => (
                            <div key={`${story.title}:${storyIndex}`} className="flex min-w-0 items-center gap-2 text-xs">
                              <span className="font-mono" style={{ color: 'var(--color-fg-muted)' }}>-</span>
                              <span className="truncate" style={{ color: 'var(--color-fg-secondary)' }}>
                                {story.title}
                              </span>
                              {story.criteria.length > 0 && (
                                <span className="ml-auto flex-shrink-0" style={{ color: 'var(--color-fg-muted)' }}>
                                  +{story.criteria.length}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  )
}

function DraftCount({
  icon: Icon,
  label,
  count,
}: {
  icon: typeof Hash
  label: string
  count: number
}) {
  return (
    <div
      className="rounded-lg px-2.5 py-2"
      style={{
        background: 'var(--color-warm-100)',
        border: '1px solid var(--color-warm-200)',
      }}
    >
      <div className="flex items-center gap-1.5" style={{ color: 'var(--color-fg-muted)' }}>
        <Icon className="h-3.5 w-3.5" />
        <span className="text-[11px] font-medium">{label}</span>
      </div>
      <div className="mt-1 text-lg font-semibold leading-none" style={{ color: 'var(--color-fg)' }}>
        {count}
      </div>
    </div>
  )
}
